import GTM from '../../../gtm/GTM';
import GTMOverviewFilterGroupToggleModel from '../../../gtm/models/GTMOverviewFilterGroupToggleModel';
import GTMViewItemListModel from '../../../gtm/models/GTMViewItemListModel';
import type { GA4SearchTermType, GTMItemData } from '../../../gtm/GTM.types';
import { convertPriceStringToNumber } from '../../../util/dataLayer';
import { calculateNumberOfNights, hasInstantBooking } from '../../../gtm/GTMHelpers';
import HouseSearchFilters from '../store/HouseSearchFilters';
import HouseSearchListItems from '../store/HouseSearchListItems';
import KeywordFilter from '../store/KeywordFilter';
import DestinationFilter from '../../../common/search/store/DestinationFilter';
import PopularPageFilter from '../store/PopularPageFilter';

export class SearchFormComponentTracking {
    readonly #filters = HouseSearchFilters.getInstance();

    readonly #items = HouseSearchListItems.getInstance();

    readonly #destinationFilter: DestinationFilter;

    readonly #keywordFilter: KeywordFilter;

    readonly #popularPageFilter: PopularPageFilter;

    public constructor() {
        this.#destinationFilter = this.#filters.get(DestinationFilter.NAME) as DestinationFilter;

        this.#keywordFilter = this.#filters.get(KeywordFilter.NAME) as KeywordFilter;

        this.#popularPageFilter = this.#filters.get(PopularPageFilter.NAME) as PopularPageFilter;
    }

    public track(): void {
        const { allParams } = this.#filters;
        const { keyword } = this.#keywordFilter.parameters || {};

        GTM.trackEcommerce(
            GTMViewItemListModel.parseToGTMData({
                listId: 'search_results',
                listName: 'Search Results',
                items: this.#parseItems(),
                searchTerm: keyword || undefined,
                searchType: keyword ? this.#getSearchTermType() : undefined,
                itemCount: this.#items.total,
                calendarType: allParams.arrivalDate ? 'fixed' : 'flexible',
                flexibleDuration: allParams.flexibleDurationType || undefined,
                flexibleMonths: allParams.flexibleBookingMonths || undefined,
                flexibleArrivalDate: allParams.flexibleArrivalDate || undefined,
                fixedDateArrival: allParams.arrivalDate || undefined,
                fixedDateDeparture: allParams.departureDate || undefined,
                adults: allParams.adults || undefined,
                children: allParams.children || undefined,
                babies: allParams.babies || undefined,
                pets: allParams.pets || undefined
            })
        );
    }

    public trackToggleFilterGroup(category?: string): void {
        GTM.track(
            GTMOverviewFilterGroupToggleModel.parseToGTMData({
                category: category || 'unknown'
            })
        );
    }

    #parseItems(): GTMItemData[] {
        return this.#items.houses.map((item): GTMItemData => {
            const priceString = item.price?.dayPrice;
            const price = convertPriceStringToNumber(priceString) ?? undefined;

            const quantity = calculateNumberOfNights({
                arrivalDate: item.price?.startDate ?? '',
                departureDate: item.price?.endDate ?? ''
            });

            return {
                id: item.id.toString(),
                title: item.title,
                country: item.country,
                region: item.region || undefined,
                department: item.department || undefined,
                place: item.place || undefined,
                discount: item.price?.discountPerNight || undefined,
                pricePerNight: price || undefined,
                instantBooking: hasInstantBooking(item.features),
                quantity: quantity || undefined
            };
        });
    }

    #getSearchTermType(): GA4SearchTermType {
        const filter = Object.entries(this.#destinationFilter.parameters).find(
            ([, value]) => value !== null
        );

        if (filter) {
            const [key] = filter;
            return key as GA4SearchTermType;
        }

        const { naturalArea } = this.#popularPageFilter.parameters;

        if (naturalArea) {
            return 'nature_area';
        }

        return 'keyword';
    }
}

export default new SearchFormComponentTracking();
